import { CABECALHO_OPERACAO, OPERACOES, type Erro, type IdOperacao } from '@bi-evox/contrato';

const CHAVE_TOKEN = 'bi-evox:token';
const CHAVE_OPERACAO = 'bi-evox:operacao';
const BASE = import.meta.env.VITE_API_URL ?? '';

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly detalhe?: string,
  ) {
    super(mensagem);
  }
}

export const token = {
  ler: (): string | null => {
    try {
      return localStorage.getItem(CHAVE_TOKEN);
    } catch {
      return null;
    }
  },
  gravar: (t: string) => {
    try {
      localStorage.setItem(CHAVE_TOKEN, t);
    } catch {
      /* navegação privada: segue só na memória da aba */
    }
    window.dispatchEvent(new Event('bi-evox:token'));
  },
  apagar: () => {
    try {
      localStorage.removeItem(CHAVE_TOKEN);
    } catch {
      /* idem */
    }
    window.dispatchEvent(new Event('bi-evox:token'));
  },
};

let operacaoNaMemoria: IdOperacao | null = null;

/** Operação escolhida no switch do topo (só admin troca); vai no cabeçalho de todo pedido. */
export const operacao = {
  ler: (): IdOperacao | null => {
    try {
      const v = localStorage.getItem(CHAVE_OPERACAO);
      return (OPERACOES as readonly string[]).includes(v ?? '') ? (v as IdOperacao) : operacaoNaMemoria;
    } catch {
      return operacaoNaMemoria;
    }
  },
  gravar: (o: IdOperacao) => {
    operacaoNaMemoria = o;
    try {
      localStorage.setItem(CHAVE_OPERACAO, o);
    } catch {
      /* navegação privada: segue só na memória da aba */
    }
    window.dispatchEvent(new Event('bi-evox:operacao'));
  },
};

/** Operação em uso; sem escolha gravada, a 99Play (a primeira de quase todo acesso). */
export const operacaoAtual = (): IdOperacao => operacao.ler() ?? '99play';

export async function obter<T>(caminho: string, params?: Record<string, string>, tokenExplicito?: string): Promise<T> {
  const qs = params ? `?${new URLSearchParams(params)}` : '';
  return chamar<T>(`${caminho}${qs}`, {}, tokenExplicito);
}

/** POST com corpo JSON: para o que não deve ir na URL (ex.: e-mail na busca de jogadores). */
export async function enviar<T>(caminho: string, corpo: unknown): Promise<T> {
  return chamar<T>(caminho, { method: 'POST', body: JSON.stringify(corpo), headers: { 'Content-Type': 'application/json' } });
}

/** POST de um arquivo cru (imagem), com o content-type do próprio arquivo. */
export async function enviarBinario<T>(caminho: string, corpo: Blob, cabecalhos: Record<string, string> = {}): Promise<T> {
  return chamar<T>(caminho, { method: 'POST', body: corpo, headers: { 'Content-Type': corpo.type, ...cabecalhos } });
}

async function chamar<T>(caminho: string, init: RequestInit, tokenExplicito?: string): Promise<T> {
  const t = tokenExplicito ?? token.ler();
  const op = operacao.ler();
  const r = await fetch(`${BASE}${caminho}`, {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
      ...(op ? { [CABECALHO_OPERACAO]: op } : {}),
    },
  });
  if (r.ok) return (await r.json()) as T;
  let corpo: Partial<Erro> = {};
  try {
    corpo = (await r.json()) as Erro;
  } catch {
    /* resposta sem JSON (ex.: 502 do proxy) */
  }
  if (r.status === 401 && !tokenExplicito) token.apagar();
  throw new ErroApi(r.status, corpo.erro ?? `erro ${r.status}`, corpo.detalhe);
}
